import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../contexts/AuthContext';
import agrofitService from '../services/agrofitService';
import Select from 'react-select';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  Save,
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Wind,
  Thermometer,
  Copy,
  Download,
  Trash2,
  File,
  Upload,
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import MapLayerControl from '../components/MapLayerControl';
import MapAttribution from '../components/MapAttribution';
import FileAttachmentList from '../components/FileAttachmentList';
import { createEsriLayer, createCartoLayer, createHybridLayer } from '../config/mapLayers';
import { getInitialLayerPreference, setLayerPreference } from '../utils/mapStorage';
import { getUserLocation } from '../utils/geolocation';
import toast from 'react-hot-toast';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const FlightForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const UFS = [
    'AC',
    'AL',
    'AM',
    'AP',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MG',
    'MS',
    'MT',
    'PA',
    'PB',
    'PE',
    'PI',
    'PR',
    'RJ',
    'RN',
    'RO',
    'RR',
    'RS',
    'SC',
    'SE',
    'SP',
    'TO',
  ];
  const [culturas, setCulturas] = useState([]);
  const [marcasComerciais, setMarcasComerciais] = useState([]);
  const [marcasComerciaisBioinsumo, setMarcasComerciaisBioinsumo] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [agrotoxicoProducts, setAgrotoxicoProducts] = useState([]);
  const [adjuvantes, setAdjuvantes] = useState([]);
  const [bioinsumoProducts, setBioinsumoProducts] = useState([]);
  const [fertilizanteProducts, setFertilizanteProducts] = useState([]);
  const [clonedFlightRawData, setClonedFlightRawData] = useState(null);
  const [applicationMapFiles, setApplicationMapFiles] = useState([]);
  const [agronomicPrescriptionFiles, setAgronomicPrescriptionFiles] = useState([]);
  const [localProductClassMap, setLocalProductClassMap] = useState({});
  const [isClonedFlight, setIsClonedFlight] = useState(false);
  const [originalFlightCode, setOriginalFlightCode] = useState('');
  const [activeMapLayer, setActiveMapLayer] = useState(() => getInitialLayerPreference());
  const [mapInstance, setMapInstance] = useState(null);
  const [currentLayer, setCurrentLayer] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const droneSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };
  const APPLICATION_FIELDS_TYPES = [
    'APLICAÇÃO DE AGROTÓXICO',
    'APLICAÇÃO DE BIOINSUMO',
    'APLICAÇÃO DE FERTILIZANTE',
    'OUTROS',
    'SEMEADURA',
    'COMBATE A INCÊNDIO',
  ];

  const CULTURE_FIELD_TYPES = [
    'APLICAÇÃO DE AGROTÓXICO',
    'APLICAÇÃO DE BIOINSUMO',
    'APLICAÇÃO DE FERTILIZANTE',
    'OUTROS',
    'SEMEADURA',
  ];
  // Unidades de dosagem com ícones (emojis) para seleção por imagem
  const DOSAGE_UNITS = [
    { value: 'g/ha', label: 'g/ha', icon: '⚖️' },
    { value: 'kg/ha', label: 'kg/ha', icon: '⚖️' },
    { value: 'L/ha', label: 'L/ha', icon: '💧' },
    { value: 'mg/ha', label: 'mg/ha', icon: '⚖️' },
    { value: 'µg/L', label: 'µg/L', icon: '💧' },
    { value: 'ml/ha', label: 'ml/ha', icon: '💧' },
    { value: 'indivíduos/ha', label: 'indivíduos/ha', icon: '👥' },
  ];
  const ADJUVANT_DOSAGE_UNITS = DOSAGE_UNITS.filter((u) => u.value !== 'indivíduos/ha');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      flight_date: new Date().toISOString().split('T')[0],
      start_time: '',
      end_time: '',
      location_name: '',
      coordinates: '',
      latitude: '',
      longitude: '',
      area_hectares: '',
      weather_conditions: '',
      wind_speed: '',
      temperature: '',
      relative_humidity: '',
      wind_direction: '',
      altitude_meters: '',
      visibility: '',
      observations: '',
      // Novos campos
      uf: '',
      municipio: '',
      aircraft_type: '',
      aircraft_prefix: '',
      drone_id: '',
      application_hours: '',
      culture: '',
      service_type: '',
      application_products: '[]',
      // Campos da aplicação
      agrotoxico_classe: '',
      agrotoxico_nome_comercial: '',
      ingrediente_ativo: '',
      concentracao: '',
      bioinsumo_classe: '',
      bioinsumo_nome_comercial: '',
      fertilizantes_outros: '',
      volume_lha: '',
      dosage_value: '',
      dosage_unit: '',
    },
  });

  // Fetch user location based on IP on component mount
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const location = await getUserLocation();
        setUserLocation(location);
        console.log('[Geolocation] User location set to:', location.city, location.country);
      } catch (error) {
        console.warn('[Geolocation] Failed to fetch user location:', error);
        // Will use default Brasília coordinates
      }
    };

    fetchUserLocation();
  }, []);

  // Load flight data for editing
  const populateFormWithFlightData = (data) => {
    Object.keys(data).forEach((key) => {
      if (key === 'flight_date') {
        setValue(key, new Date(data[key]).toISOString().split('T')[0]);
      } else if (key === 'start_time' || key === 'end_time') {
        if (data[key]) {
          setValue(key, data[key].substring(0, 5)); // HH:MM format
        }
      } else {
        setValue(key, data[key] || '');
      }
    });

    // Format coordinates field from latitude/longitude
    const lat = data.latitude ? parseFloat(data.latitude) : null;
    const lon = data.longitude ? parseFloat(data.longitude) : null;
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      setValue('coordinates', `${lat}, ${lon}`);
    }

    const productsFromApi = Array.isArray(data.application_products)
      ? data.application_products
      : [];
    if (data.service_type === 'APLICAÇÃO DE AGROTÓXICO') {
      if (productsFromApi.length > 0) {
        const adjuv = productsFromApi.filter((p) => p?.tipo_servico === 'APLICAÇÃO DE ADJUVANTE');
        const agrotox = productsFromApi.filter(
          (p) => p?.tipo_servico === 'APLICAÇÃO DE AGROTÓXICO' || !p?.tipo_servico
        );
        setAgrotoxicoProducts(agrotox);
        setAdjuvantes(
          adjuv.map((a) => ({
            nome: a?.nome_comercial || '',
            dosage_value: a?.dosage_value ?? '',
            dosage_unit: a?.dosage_unit || '',
          }))
        );
        setBioinsumoProducts([]);
        setFertilizanteProducts([]);
        setValue('application_products', JSON.stringify(productsFromApi));
      } else if (
        data.agrotoxico_nome_comercial ||
        data.agrotoxico_classe ||
        data.dosage_value ||
        data.dosage_unit
      ) {
        const initial = [
          {
            nome_comercial: data.agrotoxico_nome_comercial || '',
            classe: data.agrotoxico_classe || '',
            dosage_value: data.dosage_value || '',
            dosage_unit: data.dosage_unit || '',
          },
        ];
        setAgrotoxicoProducts(initial);
        setAdjuvantes([]);
        setBioinsumoProducts([]);
        setFertilizanteProducts([]);
        setValue('application_products', JSON.stringify(initial));
      } else {
        setAgrotoxicoProducts([]);
        setAdjuvantes([]);
        setBioinsumoProducts([]);
        setFertilizanteProducts([]);
        setValue('application_products', '[]');
      }
    } else if (data.service_type === 'APLICAÇÃO DE BIOINSUMO') {
      const bios = productsFromApi.filter((p) => p?.tipo_servico === 'APLICAÇÃO DE BIOINSUMO');
      const initial =
        bios.length > 0
          ? bios.map((p) => ({
              nome_comercial: p?.nome_comercial || '',
              classe: p?.classe || '',
              dosage_value: p?.dosage_value ?? '',
              dosage_unit: p?.dosage_unit || '',
            }))
          : [
              {
                nome_comercial: data.bioinsumo_nome_comercial || '',
                classe: data.bioinsumo_classe || '',
                dosage_value: data.dosage_value || '',
                dosage_unit: data.dosage_unit || '',
              },
            ].filter((p) => p.nome_comercial || p.classe || p.dosage_value || p.dosage_unit);
      setBioinsumoProducts(
        initial.length
          ? initial
          : [{ nome_comercial: '', classe: '', dosage_value: '', dosage_unit: '' }]
      );
      setAgrotoxicoProducts([]);
      setAdjuvantes([]);
      setFertilizanteProducts([]);
      setValue(
        'application_products',
        productsFromApi.length ? JSON.stringify(productsFromApi) : JSON.stringify(initial)
      );
    } else if (
      data.service_type === 'APLICAÇÃO DE FERTILIZANTE' ||
      data.service_type === 'OUTROS'
    ) {
      const ferts = productsFromApi.filter((p) => p?.tipo_servico === data.service_type);
      const initial =
        ferts.length > 0
          ? ferts.map((p) => ({
              nome_comercial: p?.nome_comercial || '',
              dosage_value: p?.dosage_value ?? '',
              dosage_unit: p?.dosage_unit || '',
            }))
          : [
              {
                nome_comercial: data.fertilizantes_outros || '',
                dosage_value: data.dosage_value || '',
                dosage_unit: data.dosage_unit || '',
              },
            ].filter((p) => p.nome_comercial || p.dosage_value || p.dosage_unit);
      setFertilizanteProducts(
        initial.length ? initial : [{ nome_comercial: '', dosage_value: '', dosage_unit: '' }]
      );
      setAgrotoxicoProducts([]);
      setAdjuvantes([]);
      setBioinsumoProducts([]);
      setValue(
        'application_products',
        productsFromApi.length ? JSON.stringify(productsFromApi) : JSON.stringify(initial)
      );
    } else if (
      data.agrotoxico_nome_comercial ||
      data.agrotoxico_classe ||
      data.dosage_value ||
      data.dosage_unit
    ) {
      const initial = [
        {
          nome_comercial: data.agrotoxico_nome_comercial || '',
          classe: data.agrotoxico_classe || '',
          dosage_value: data.dosage_value || '',
          dosage_unit: data.dosage_unit || '',
        },
      ];
      setAgrotoxicoProducts(initial);
      setAdjuvantes([]);
      setBioinsumoProducts([]);
      setFertilizanteProducts([]);
      setValue('application_products', JSON.stringify(initial));
    } else {
      setAgrotoxicoProducts([]);
      setAdjuvantes([]);
      setBioinsumoProducts([]);
      setFertilizanteProducts([]);
      setValue('application_products', '[]');
    }
  };

  const { data: flightData, isLoading: loadingFlight } = useQuery(
    ['flight', id],
    () => api.get(`/flights/${id}`).then((res) => res.data),
    {
      enabled: isEdit,
      refetchOnMount: 'always',
    }
  );

  useEffect(() => {
    if (flightData) {
      populateFormWithFlightData(flightData);
    }
  }, [flightData]);

  // Detect clone mode and populate form with cloned data
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isClone = params.get('clone') === 'true';

    if (isClone) {
      const clonedData = sessionStorage.getItem('clonedFlightData');
      const clonedId = sessionStorage.getItem('clonedFlightId');

      if (clonedData) {
        const data = JSON.parse(clonedData);
        setIsClonedFlight(true);
        setOriginalFlightCode(`#${clonedId}`);

        // Armazenar dados clonados para processamento posterior (quando marcasComerciais estiver carregado)
        setClonedFlightRawData(data);

        // Preencher formulário com dados clonados (exceto produtos que serão processados depois)
        Object.keys(data).forEach((key) => {
          if (key !== 'application_products' && data[key] !== null && data[key] !== undefined) {
            setValue(key, data[key]);
          }
        });

        // Limpar sessionStorage imediatamente para evitar processamento duplicado
        sessionStorage.removeItem('clonedFlightData');
        sessionStorage.removeItem('clonedFlightId');
      }
    }
  }, [setValue]);

  // Processar produtos clonados (não depende mais de marcas comerciais)
  useEffect(() => {
    if (!clonedFlightRawData) return;

    const data = clonedFlightRawData;

    // DEBUG: Log para entender o que está sendo processado
    console.log('🔍 [CLONE DEBUG] Processando produtos clonados:', {
      service_type: data.service_type,
      service_type_type: typeof data.service_type,
      application_products: data.application_products,
      application_products_type: typeof data.application_products,
    });

    // Handle application_products array (SEM depender de marcas comerciais)
    if (data.application_products) {
      try {
        const products =
          typeof data.application_products === 'string'
            ? JSON.parse(data.application_products)
            : data.application_products;

        console.log('📦 [CLONE DEBUG] Produtos parseados:', {
          is_array: Array.isArray(products),
          length: products?.length,
          products: products,
        });

        if (Array.isArray(products) && products.length > 0) {
          if (data.service_type === 'APLICAÇÃO DE AGROTÓXICO') {
            const adjuv = products.filter((p) => p?.tipo_servico === 'APLICAÇÃO DE ADJUVANTE');
            const agrotox = products.filter(
              (p) => p?.tipo_servico === 'APLICAÇÃO DE AGROTÓXICO' || !p?.tipo_servico
            );
            console.log('🌱 [CLONE DEBUG] AGROTÓXICO:', { adjuv, agrotox });
            setAgrotoxicoProducts(agrotox);
            setAdjuvantes(
              adjuv.map((a) => ({
                nome: a?.nome_comercial || '',
                dosage_value: a?.dosage_value ?? '',
                dosage_unit: a?.dosage_unit || '',
              }))
            );
          } else if (data.service_type === 'APLICAÇÃO DE BIOINSUMO') {
            const bios = products.filter((p) => p?.tipo_servico === 'APLICAÇÃO DE BIOINSUMO');
            const initial = bios.map((p) => ({
              nome_comercial: p?.nome_comercial || '',
              classe: p?.classe || '',
              dosage_value: p?.dosage_value ?? '',
              dosage_unit: p?.dosage_unit || '',
            }));
            console.log('🌿 [CLONE DEBUG] BIOINSUMO:', { bios, initial });
            setBioinsumoProducts(
              initial.length
                ? initial
                : [{ nome_comercial: '', classe: '', dosage_value: '', dosage_unit: '' }]
            );
          } else if (
            data.service_type === 'APLICAÇÃO DE FERTILIZANTE' ||
            data.service_type === 'OUTROS'
          ) {
            const ferts = products.filter((p) => p?.tipo_servico === data.service_type);
            const initial = ferts.map((p) => ({
              nome_comercial: p?.nome_comercial || '',
              dosage_value: p?.dosage_value ?? '',
              dosage_unit: p?.dosage_unit || '',
            }));
            console.log('🧪 [CLONE DEBUG] FERTILIZANTE/OUTROS:', { ferts, initial });
            setFertilizanteProducts(
              initial.length ? initial : [{ nome_comercial: '', dosage_value: '', dosage_unit: '' }]
            );
          }
        } else {
          console.log('⚠️ [CLONE DEBUG] Nenhum produto encontrado ou array vazio');
        }
      } catch (e) {
        console.error('❌ [CLONE DEBUG] Error parsing application_products:', e);
      }
    } else {
      console.log('⚠️ [CLONE DEBUG] Sem application_products nos dados clonados');
    }

    // Limpar dados brutos após processamento
    setClonedFlightRawData(null);
  }, [clonedFlightRawData]);

  useEffect(() => {
    const loadDadosAuxiliares = async () => {
      try {
        const dadosBasicos = await agrofitService.getDadosBasicos();
        const listaCulturas = (dadosBasicos?.culturas || []).map((c) => c.nome || c);
        setCulturas(listaCulturas);

        const [listaMarcasAgrotoxico, listaMarcasBioinsumo] = await Promise.all([
          agrofitService.getMarcasComerciais({ tipo: 'agrotoxico' }),
          agrofitService.getMarcasComerciais({ tipo: 'bioinsumo' }),
        ]);

        const parsedMarcas = (listaMarcasAgrotoxico || [])
          .map((m) => {
            const nome = m?.l || m?.nome || m?.marca || m?.produto || m;
            const classe =
              m?.c || m?.classe_agronomica || m?.classe || m?.classe_agrotoxicos || null;
            return { label: nome, value: nome, classe };
          })
          .filter((item) => Boolean(item.label));

        setMarcasComerciais(parsedMarcas);

        const parsedBioinsumos = (listaMarcasBioinsumo || [])
          .map((m) => {
            const nome = m?.l || m?.nome || m?.marca || m?.produto || m;
            const classe =
              m?.c || m?.classe_agronomica || m?.classe || m?.classe_agrotoxicos || null;
            return { label: nome, value: nome, classe };
          })
          .filter((item) => Boolean(item.label));
        setMarcasComerciaisBioinsumo(parsedBioinsumos);
      } catch (error) {
        console.error('Erro ao carregar dados auxiliares Agrofit:', error);
      }
    };

    loadDadosAuxiliares();
  }, []);

  useEffect(() => {
    const loadLocalProductClassMap = async () => {
      try {
        const response = await api.get('/flights/product-class-map');
        setLocalProductClassMap(
          response.data && typeof response.data === 'object' ? response.data : {}
        );
      } catch {
        setLocalProductClassMap({});
      }
    };
    loadLocalProductClassMap();
  }, []);

  const { data: dronesData } = useQuery(
    ['drones'],
    () => api.get('/drones?limit=100').then((res) => res.data),
    { staleTime: 5 * 60 * 1000 }
  );

  const droneOptions = (dronesData?.drones || []).map((d) => ({
    value: d.id,
    label: d.prefix ? `${d.name || ''} - ${d.prefix}` : d.name || '',
    prefix: d.prefix || '',
  }));

  const selectedUf = watch('uf');
  const selectedMunicipio = watch('municipio');

  const { data: municipiosData, isLoading: loadingMunicipios } = useQuery(
    ['ibgeMunicipios', selectedUf],
    () => api.get(`/ibge/municipios?uf=${selectedUf}`).then((res) => res.data),
    { enabled: Boolean(selectedUf), staleTime: 24 * 60 * 60 * 1000 }
  );

  const municipioOptions = (municipiosData?.municipios || []).map((m) => ({ value: m, label: m }));
  const selectedMunicipioOption = selectedMunicipio
    ? municipioOptions.find((opt) => opt.value === selectedMunicipio) || {
        value: selectedMunicipio,
        label: selectedMunicipio,
      }
    : null;

  // Helper function to parse attachment arrays from flightData
  const getAttachmentList = (field) => {
    if (!flightData) return [];
    const value = flightData[field];
    if (!value) return [];
    // Handle both JSON arrays and comma-separated strings
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        // If not JSON, try comma-separated
        return value.split(',').filter(Boolean);
      }
    }
    return Array.isArray(value) ? value : [];
  };

  const applicationMaps = getAttachmentList('application_maps_names');
  const agronomicPrescriptions = getAttachmentList('agronomic_prescriptions_names');

  // Helper function to handle file selection with validation
  const handleFileSelection = (kind, newFiles, currentFiles, setter) => {
    const MAX_FILES = 5;
    const MAX_SIZE_MB = 20;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

    const totalFiles = currentFiles.length + newFiles.length;
    if (totalFiles > MAX_FILES) {
      toast.error(`Máximo de ${MAX_FILES} arquivos permitidos por tipo`);
      return;
    }

    const validFiles = [];
    const invalidFiles = [];

    Array.from(newFiles).forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        invalidFiles.push({ file, reason: 'tipo inválido' });
      } else if (file.size > MAX_SIZE_BYTES) {
        invalidFiles.push({ file, reason: 'muito grande (máx 20MB)' });
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      const reasons = invalidFiles.map((f) => `"${f.file.name}": ${f.reason}`).join(', ');
      toast.error(`Arquivos inválidos: ${reasons}`);
    }

    if (validFiles.length > 0) {
      setter([...currentFiles, ...validFiles]);
    }
  };

  const uploadAttachments = async (flightId) => {
    const formData = new FormData();
    applicationMapFiles.forEach((file) => formData.append('application_map', file));
    agronomicPrescriptionFiles.forEach((file) => formData.append('agronomic_prescription', file));
    if (applicationMapFiles.length === 0 && agronomicPrescriptionFiles.length === 0) return;
    await api.post(`/flights/${flightId}/attachments`, formData);
  };

  const parseContentDispositionFileName = (headerValue) => {
    const raw = headerValue ? String(headerValue) : '';
    const match = raw.match(/filename\*?=(?:UTF-8''|\"?)([^\";]+)/i);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  };

  const downloadAttachment = async (kind, index = 0) => {
    const flightId = id;
    if (!flightId) return;
    const res = await api.get(`/flights/${flightId}/attachments/${kind}/${index}`, {
      responseType: 'blob',
    });
    const suggestedName = parseContentDispositionFileName(res.headers?.['content-disposition']);
    const fileName =
      suggestedName ||
      (kind === 'application_map' ? 'mapa_de_aplicacao' : 'receituario_agronomico');
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const deleteAttachment = async (kind, index = 0) => {
    const flightId = id;
    if (!flightId) return;
    try {
      await api.delete(`/flights/${flightId}/attachments/${kind}/${index}`);
      queryClient.invalidateQueries(['flight', id]);
      toast.success('Arquivo excluído com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir anexo');
    }
  };

  const downloadFlightPdf = async (flight) => {
    const flightId = flight?.id || id;
    if (!flightId) return;
    const flightDate = flight?.flight_date || watch('flight_date');
    const dateText = flightDate ? String(flightDate).replace(/-/g, '') : '';
    const fileName = `voo_${flightId}${dateText ? `_${dateText}` : ''}.pdf`;
    const res = await api.get(`/flights/${flightId}/pdf`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  };

  // Create flight mutation
  const createMutation = useMutation(
    async (data) => {
      const res = await api.post('/flights', data);
      const flight = res.data;
      await uploadAttachments(flight.id);
      return flight;
    },
    {
      onSuccess: async (flight) => {
        queryClient.invalidateQueries('flights');
        queryClient.invalidateQueries('dashboard');
        setApplicationMapFiles([]);
        setAgronomicPrescriptionFiles([]);
        if (isClonedFlight && originalFlightCode) {
          toast.success(`Voo criado com sucesso (baseado em ${originalFlightCode})!`);
        } else {
          toast.success('Voo registrado com sucesso!');
        }
        navigate('/flights');
      },
      onError: (error) => {
        const serverError = error?.response?.data?.error || error?.response?.data?.message;
        toast.error(serverError || 'Erro ao registrar voo');
      },
    }
  );

  // Update flight mutation
  const updateMutation = useMutation(
    async (data) => {
      const res = await api.put(`/flights/${id}`, data);
      const flight = res.data;
      await uploadAttachments(flight.id);
      return flight;
    },
    {
      onSuccess: async (flight) => {
        queryClient.invalidateQueries('flights');
        queryClient.invalidateQueries(['flight', id]);
        queryClient.invalidateQueries('dashboard');
        setApplicationMapFiles([]);
        setAgronomicPrescriptionFiles([]);
        toast.success('Voo atualizado com sucesso!');
        navigate('/flights');
      },
      onError: (error) => {
        const serverError = error?.response?.data?.error || error?.response?.data?.message;
        toast.error(serverError || 'Erro ao atualizar voo');
      },
    }
  );

  // Integração Agrofit de busca removida; preencher campos manualmente.

  // Calculate duration automatically
  const startTime = watch('start_time');
  const endTime = watch('end_time');
  const serviceType = watch('service_type');
  const selectedDroneId = watch('drone_id');
  const selectedDroneOption =
    droneOptions.find((opt) => String(opt.value) === String(selectedDroneId)) || null;
  const operationSectionTitle =
    serviceType === 'APLICAÇÃO DE AGROTÓXICO' ||
    serviceType === 'APLICAÇÃO DE BIOINSUMO' ||
    serviceType === 'APLICAÇÃO DE FERTILIZANTE'
      ? 'Dados da Aplicação'
      : 'Dados da Operação';

  useEffect(() => {
    if (!selectedDroneId) {
      setValue('aircraft_type', 'Drone');
      setValue('aircraft_prefix', '');
      return;
    }
    setValue('aircraft_type', 'Drone');
    if (selectedDroneOption?.prefix) {
      setValue('aircraft_prefix', selectedDroneOption.prefix);
    }
  }, [selectedDroneId, selectedDroneOption, setValue]);

  useEffect(() => {
    if (startTime && endTime) {
      const start = new Date(`2000-01-01T${startTime}:00`);
      const end = new Date(`2000-01-01T${endTime}:00`);

      if (end > start) {
        const diffMs = end - start;
        const diffMins = Math.floor(diffMs / 60000);
        setValue('duration_minutes', diffMins);
        const horas = (diffMins / 60).toFixed(2);
        if (APPLICATION_FIELDS_TYPES.includes(serviceType)) setValue('application_hours', horas);
        else setValue('application_hours', '');
      }
    }
  }, [startTime, endTime, setValue, serviceType]);

  const normalizeKey = (value) =>
    String(value || '')
      .trim()
      .toUpperCase();

  useEffect(() => {
    if (serviceType === 'APLICAÇÃO DE AGROTÓXICO' && agrotoxicoProducts.length === 0) {
      const initial = [
        {
          nome_comercial: watch('agrotoxico_nome_comercial') || '',
          classe: watch('agrotoxico_classe') || '',
          dosage_value: watch('dosage_value') || '',
          dosage_unit: watch('dosage_unit') || '',
        },
      ];
      setAgrotoxicoProducts(initial);
      setAdjuvantes([]);
      setValue(
        'application_products',
        JSON.stringify(initial.map((p) => ({ ...p, tipo_servico: 'APLICAÇÃO DE AGROTÓXICO' })))
      );
    }
    if (
      serviceType !== 'APLICAÇÃO DE AGROTÓXICO' &&
      (agrotoxicoProducts.length > 0 || adjuvantes.length > 0)
    ) {
      setAgrotoxicoProducts([]);
      setAdjuvantes([]);
    }
  }, [serviceType]);

  useEffect(() => {
    if (serviceType !== 'APLICAÇÃO DE AGROTÓXICO') return;
    const combined = [
      ...agrotoxicoProducts.map((p) => ({ ...p, tipo_servico: 'APLICAÇÃO DE AGROTÓXICO' })),
      ...adjuvantes.map((a) => ({
        nome_comercial: a?.nome || '',
        classe: '',
        dosage_value: a?.dosage_value ?? '',
        dosage_unit: a?.dosage_unit || '',
        tipo_servico: 'APLICAÇÃO DE ADJUVANTE',
      })),
    ];
    setValue('application_products', JSON.stringify(combined));
    const first = agrotoxicoProducts[0] || {};
    setValue('agrotoxico_nome_comercial', first.nome_comercial || '');
    setValue('agrotoxico_classe', first.classe || '');
    setValue('dosage_value', first.dosage_value || '');
    setValue('dosage_unit', first.dosage_unit || '');
  }, [agrotoxicoProducts, adjuvantes, serviceType, setValue]);

  useEffect(() => {
    if (serviceType === 'APLICAÇÃO DE BIOINSUMO' && bioinsumoProducts.length === 0) {
      const initial = [
        {
          nome_comercial: watch('bioinsumo_nome_comercial') || '',
          classe: watch('bioinsumo_classe') || '',
          dosage_value: watch('dosage_value') || '',
          dosage_unit: watch('dosage_unit') || '',
        },
      ];
      setBioinsumoProducts(initial);
      setFertilizanteProducts([]);
      setValue(
        'application_products',
        JSON.stringify(initial.map((p) => ({ ...p, tipo_servico: 'APLICAÇÃO DE BIOINSUMO' })))
      );
    }
    if (serviceType !== 'APLICAÇÃO DE BIOINSUMO' && bioinsumoProducts.length > 0) {
      setBioinsumoProducts([]);
    }
  }, [serviceType]);

  useEffect(() => {
    if (serviceType !== 'APLICAÇÃO DE BIOINSUMO') return;
    const combined = bioinsumoProducts.map((p) => ({
      ...p,
      tipo_servico: 'APLICAÇÃO DE BIOINSUMO',
    }));
    setValue('application_products', JSON.stringify(combined));
    const first = bioinsumoProducts[0] || {};
    setValue('bioinsumo_nome_comercial', first.nome_comercial || '');
    setValue('bioinsumo_classe', first.classe || '');
    setValue('dosage_value', first.dosage_value || '');
    setValue('dosage_unit', first.dosage_unit || '');
  }, [bioinsumoProducts, serviceType, setValue]);

  useEffect(() => {
    if (
      (serviceType === 'APLICAÇÃO DE FERTILIZANTE' || serviceType === 'OUTROS') &&
      fertilizanteProducts.length === 0
    ) {
      const initial = [
        {
          nome_comercial: watch('fertilizantes_outros') || '',
          dosage_value: watch('dosage_value') || '',
          dosage_unit: watch('dosage_unit') || '',
        },
      ];
      setFertilizanteProducts(initial);
      setBioinsumoProducts([]);
      setValue(
        'application_products',
        JSON.stringify(initial.map((p) => ({ ...p, tipo_servico: serviceType })))
      );
    }
    if (serviceType !== 'APLICAÇÃO DE FERTILIZANTE' && serviceType !== 'OUTROS') {
      if (fertilizanteProducts.length > 0) setFertilizanteProducts([]);
    }
  }, [serviceType]);

  useEffect(() => {
    if (serviceType !== 'APLICAÇÃO DE FERTILIZANTE' && serviceType !== 'OUTROS') return;
    const combined = fertilizanteProducts.map((p) => ({ ...p, tipo_servico: serviceType }));
    setValue('application_products', JSON.stringify(combined));
    const first = fertilizanteProducts[0] || {};
    setValue('fertilizantes_outros', first.nome_comercial || '');
    setValue('dosage_value', first.dosage_value || '');
    setValue('dosage_unit', first.dosage_unit || '');
  }, [fertilizanteProducts, serviceType, setValue]);

  const parseCoordinates = (value) => {
    if (!value) return null;
    const parts = String(value)
      .split(',')
      .map((p) => p.trim());
    if (parts.length !== 2) return null;
    const latitude = parseFloat(parts[0]);
    const longitude = parseFloat(parts[1]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
    return { latitude, longitude };
  };

  const onSubmit = (data) => {
    if (data.service_type === 'APLICAÇÃO DE AGROTÓXICO') {
      for (let i = 0; i < agrotoxicoProducts.length; i++) {
        const p = agrotoxicoProducts[i] || {};
        if (!p?.dosage_value) {
          toast.error(`Preencha o valor da dosagem do Produto ${i + 1}`);
          return;
        }
        if (!p?.dosage_unit) {
          toast.error(`Selecione a unidade de dosagem do Produto ${i + 1}`);
          return;
        }
      }
      for (let i = 0; i < adjuvantes.length; i++) {
        const a = adjuvantes[i] || {};
        if (a?.nome && !a?.dosage_value) {
          toast.error(`Preencha o valor da dosagem do Adjuvante ${i + 1}`);
          return;
        }
        if (a?.nome && !a?.dosage_unit) {
          toast.error(`Selecione a unidade de dosagem do Adjuvante ${i + 1}`);
          return;
        }
      }
    }
    if (data.service_type === 'APLICAÇÃO DE BIOINSUMO') {
      if (!Array.isArray(bioinsumoProducts) || bioinsumoProducts.length === 0) {
        toast.error('Adicione pelo menos um bioinsumo');
        return;
      }
      for (let i = 0; i < bioinsumoProducts.length; i++) {
        const p = bioinsumoProducts[i] || {};
        if (!p?.nome_comercial) {
          toast.error(`Selecione o Nome Comercial do Bioinsumo ${i + 1}`);
          return;
        }
        if (!p?.classe) {
          toast.error(`A Classe do Bioinsumo ${i + 1} deve ser preenchida`);
          return;
        }
        if (!p?.dosage_value) {
          toast.error(`Preencha o valor da dosagem do Bioinsumo ${i + 1}`);
          return;
        }
        if (!p?.dosage_unit) {
          toast.error(`Selecione a unidade de dosagem do Bioinsumo ${i + 1}`);
          return;
        }
      }
    }

    if (data.service_type === 'APLICAÇÃO DE FERTILIZANTE' || data.service_type === 'OUTROS') {
      if (!Array.isArray(fertilizanteProducts) || fertilizanteProducts.length === 0) {
        toast.error('Adicione pelo menos um fertilizante/outro');
        return;
      }
      for (let i = 0; i < fertilizanteProducts.length; i++) {
        const p = fertilizanteProducts[i] || {};
        if (!p?.nome_comercial) {
          toast.error(`Preencha o nome do item ${i + 1}`);
          return;
        }
        if (!p?.dosage_value) {
          toast.error(`Preencha o valor da dosagem do item ${i + 1}`);
          return;
        }
        if (!p?.dosage_unit) {
          toast.error(`Selecione a unidade de dosagem do item ${i + 1}`);
          return;
        }
      }
    }

    // Calculate duration if not already set
    if (data.start_time && data.end_time) {
      const start = new Date(`2000-01-01T${data.start_time}:00`);
      const end = new Date(`2000-01-01T${data.end_time}:00`);

      if (end > start) {
        const diffMs = end - start;
        data.duration_minutes = Math.floor(diffMs / 60000);
      }
    }

    let parsedProducts = [];
    if (data.application_products) {
      try {
        parsedProducts = JSON.parse(data.application_products);
      } catch {
        parsedProducts = [];
      }
    }
    if (!Array.isArray(parsedProducts)) parsedProducts = [];
    if (data.service_type === 'APLICAÇÃO DE AGROTÓXICO') {
      data.application_products = parsedProducts
        .map((p) => {
          const tipo =
            p?.tipo_servico === 'APLICAÇÃO DE ADJUVANTE'
              ? 'APLICAÇÃO DE ADJUVANTE'
              : 'APLICAÇÃO DE AGROTÓXICO';
          return {
            tipo_servico: tipo,
            nome_comercial: p?.nome_comercial || '',
            classe: tipo === 'APLICAÇÃO DE ADJUVANTE' ? '' : p?.classe || '',
            dosage_value:
              p?.dosage_value === '' || p?.dosage_value === null || p?.dosage_value === undefined
                ? null
                : parseFloat(p.dosage_value),
            dosage_unit: p?.dosage_unit || '',
          };
        })
        .filter((p) => p.nome_comercial || p.classe || p.dosage_value !== null || p.dosage_unit);
    } else if (data.service_type === 'APLICAÇÃO DE BIOINSUMO') {
      const normalized = (bioinsumoProducts || []).map((p) => ({
        tipo_servico: 'APLICAÇÃO DE BIOINSUMO',
        nome_comercial: String(p?.nome_comercial || '').trim(),
        classe: String(p?.classe || '').trim(),
        dosage_value:
          p?.dosage_value === '' || p?.dosage_value === null || p?.dosage_value === undefined
            ? null
            : parseFloat(p.dosage_value),
        dosage_unit: String(p?.dosage_unit || '').trim(),
      }));
      data.application_products = normalized.filter(
        (p) => p.nome_comercial || p.classe || p.dosage_value !== null || p.dosage_unit
      );
      const first = data.application_products[0] || {};
      data.bioinsumo_nome_comercial = first.nome_comercial || '';
      data.bioinsumo_classe = first.classe || '';
      data.dosage_value = first.dosage_value ?? null;
      data.dosage_unit = first.dosage_unit || '';
    } else if (
      data.service_type === 'APLICAÇÃO DE FERTILIZANTE' ||
      data.service_type === 'OUTROS'
    ) {
      const normalized = (fertilizanteProducts || []).map((p) => ({
        tipo_servico: data.service_type,
        nome_comercial: String(p?.nome_comercial || '').trim(),
        classe: '',
        dosage_value:
          p?.dosage_value === '' || p?.dosage_value === null || p?.dosage_value === undefined
            ? null
            : parseFloat(p.dosage_value),
        dosage_unit: String(p?.dosage_unit || '').trim(),
      }));
      data.application_products = normalized.filter(
        (p) => p.nome_comercial || p.dosage_value !== null || p.dosage_unit
      );
      const first = data.application_products[0] || {};
      data.fertilizantes_outros = first.nome_comercial || '';
      data.dosage_value = first.dosage_value ?? null;
      data.dosage_unit = first.dosage_unit || '';
    } else {
      data.application_products = [];
    }

    // Convert numeric fields
    const parsedCoords = parseCoordinates(data.coordinates);
    const parsedLat = data.latitude ? parseFloat(data.latitude) : parsedCoords?.latitude;
    const parsedLon = data.longitude ? parseFloat(data.longitude) : parsedCoords?.longitude;
    if (Number.isFinite(parsedLat) && Number.isFinite(parsedLon)) {
      data.latitude = parsedLat;
      data.longitude = parsedLon;
    } else {
      delete data.latitude;
      delete data.longitude;
    }
    if (data.drone_id) {
      data.drone_id = parseInt(data.drone_id, 10);
    } else {
      data.drone_id = null;
    }
    if (data.area_hectares) {
      data.area_hectares = parseFloat(data.area_hectares);
    }
    if (data.wind_speed) {
      data.wind_speed_kmh = parseFloat(data.wind_speed);
    }
    if (data.temperature) {
      data.temperature_celsius = parseFloat(data.temperature);
    }
    if (data.relative_humidity) {
      data.relative_humidity = parseFloat(data.relative_humidity);
    }
    if (data.wind_direction) {
      data.wind_direction = String(data.wind_direction).trim();
    }
    if (data.altitude_meters) {
      data.altitude_meters = parseInt(data.altitude_meters, 10);
    }
    if (data.application_hours) {
      data.application_hours = parseFloat(data.application_hours);
    } else if (data.duration_minutes) {
      data.application_hours = parseFloat((data.duration_minutes / 60).toFixed(2));
    }

    // Campos numéricos adicionais
    if (data.volume_lha) data.volume_lha = parseFloat(data.volume_lha);
    if (data.dosage_value) data.dosage_value = parseFloat(data.dosage_value);

    // limpar campos auxiliares para evitar conflito de nomes
    delete data.wind_speed;
    delete data.temperature;
    delete data.weather_conditions;
    delete data.visibility;

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  if (isEdit && loadingFlight) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const watchedLatitude = watch('latitude');
  const watchedLongitude = watch('longitude');
  const watchedCoordinates = watch('coordinates');
  const parsedWatchedCoords = parseCoordinates(watchedCoordinates);
  const parsedWatchedLat = watchedLatitude
    ? parseFloat(watchedLatitude)
    : parsedWatchedCoords?.latitude;
  const parsedWatchedLon = watchedLongitude
    ? parseFloat(watchedLongitude)
    : parsedWatchedCoords?.longitude;
  const mapCenter =
    Number.isFinite(parsedWatchedLat) && Number.isFinite(parsedWatchedLon)
      ? [parsedWatchedLat, parsedWatchedLon]
      : userLocation
        ? [userLocation.lat, userLocation.lon]
        : [-15.793889, -47.882778];
  const mapMarker =
    Number.isFinite(parsedWatchedLat) && Number.isFinite(parsedWatchedLon)
      ? [parsedWatchedLat, parsedWatchedLon]
      : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button onClick={() => navigate('/flights')} className="btn-secondary">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit
              ? `Editar Voo${flightData?.public_code ? ` (${flightData.public_code})` : ''}`
              : 'Novo Voo'}
          </h1>
          <p className="text-gray-600">
            {isEdit ? 'Atualize as informações do voo' : 'Registre um novo voo de drone'}
          </p>
        </div>
      </div>

      {/* Clone Banner */}
      {isClonedFlight && (
        <div
          data-testid="clone-banner"
          className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4"
        >
          <div className="flex items-center">
            <Copy className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Voo clonado de <span className="font-mono">{originalFlightCode}</span>
              </p>
              <p className="text-xs text-green-600">
                Edite os campos necessários e salve para criar o novo registro
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Informações Básicas</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Data do Voo *
                </label>
                <input
                  data-testid="flight-date"
                  type="date"
                  className={`input ${errors.flight_date ? 'border-red-500' : ''}`}
                  {...register('flight_date', { required: 'Data é obrigatória' })}
                />
                {errors.flight_date && (
                  <p className="text-red-500 text-sm mt-1">{errors.flight_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Hora de Início *
                </label>
                <input
                  data-testid="start-time"
                  type="time"
                  className={`input ${errors.start_time ? 'border-red-500' : ''}`}
                  {...register('start_time', {
                    required: 'Hora de início é obrigatória',
                  })}
                />
                {errors.start_time && (
                  <p className="text-red-500 text-sm mt-1">{errors.start_time.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Hora de Término *
                </label>
                <input
                  data-testid="end-time"
                  type="time"
                  className={`input ${errors.end_time ? 'border-red-500' : ''}`}
                  {...register('end_time', {
                    required: 'Hora de término é obrigatória',
                  })}
                />
                {errors.end_time && (
                  <p className="text-red-500 text-sm mt-1">{errors.end_time.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Drone</label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Selecione um drone cadastrado..."
                  options={droneOptions}
                  value={selectedDroneOption}
                  styles={droneSelectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(opt) => {
                    if (!opt) {
                      setValue('drone_id', '');
                      setValue('aircraft_prefix', '');
                      return;
                    }
                    setValue('drone_id', String(opt.value));
                    setValue('aircraft_prefix', opt.prefix || '');
                    setValue('aircraft_type', 'Drone');
                  }}
                  isClearable
                  isSearchable
                />
                <input
                  type="hidden"
                  {...register('drone_id', { required: 'Selecione um drone' })}
                />
                <input type="hidden" {...register('aircraft_prefix')} />
                <input type="hidden" {...register('aircraft_type')} />
                {errors.drone_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.drone_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Altura de Voo (m)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="input"
                  placeholder="Ex: 30"
                  {...register('altitude_meters')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Serviço *
                </label>
                <select
                  className={`input ${errors.service_type ? 'border-red-500' : ''}`}
                  {...register('service_type', { required: 'Tipo de serviço é obrigatório' })}
                >
                  <option value="">Selecione...</option>
                  <option value="APLICAÇÃO DE AGROTÓXICO">APLICAÇÃO DE AGROTÓXICO</option>
                  <option value="APLICAÇÃO DE BIOINSUMO">APLICAÇÃO DE BIOINSUMO</option>
                  <option value="APLICAÇÃO DE FERTILIZANTE">APLICAÇÃO DE FERTILIZANTE</option>
                  <option value="SEMEADURA">SEMEADURA</option>
                  <option value="COMBATE A INCÊNDIO" disabled>
                    COMBATE A INCÊNDIO (desabilitado)
                  </option>
                  <option value="POVOAMENTO DE ÁGUAS" disabled>
                    POVOAMENTO DE ÁGUAS (desabilitado)
                  </option>
                  <option value="OUTROS">OUTROS</option>
                </select>
                {errors.service_type && (
                  <p className="text-red-500 text-sm mt-1">{errors.service_type.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Localização</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Nome do Cliente
                </label>
                <input
                  type="text"
                  className={`input ${errors.location_name ? 'border-red-500' : ''}`}
                  placeholder="Ex: Fazenda São João, Talhão 5"
                  {...register('location_name')}
                />
                {errors.location_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.location_name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coordenadas</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="Ex: -15.123456, -47.654321"
                      {...register('coordinates')}
                    />
                    <button
                      type="button"
                      className="btn-secondary whitespace-nowrap"
                      onClick={() => setShowMapModal(true)}
                    >
                      Mapa
                    </button>
                  </div>
                  <input type="hidden" {...register('latitude')} />
                  <input type="hidden" {...register('longitude')} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Área (hectares) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={`input ${errors.area_hectares ? 'border-red-500' : ''}`}
                  placeholder="Ex: 10.5"
                  {...register('area_hectares', { required: 'Área é obrigatoria' })}
                />
                {errors.area_hectares && (
                  <p className="text-red-500 text-sm mt-1">{errors.area_hectares.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UF *</label>
                <select
                  className={`input ${errors.uf ? 'border-red-500' : ''}`}
                  {...register('uf', {
                    required: 'UF é obrigatoria',
                    onChange: () => {
                      setValue('municipio', '');
                    },
                  })}
                >
                  <option value="">Selecione...</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
                {errors.uf && <p className="text-red-500 text-sm mt-1">{errors.uf.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Município *</label>
                {selectedUf ? (
                  <div>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder={
                        loadingMunicipios ? 'Carregando municípios...' : 'Selecione um município...'
                      }
                      options={municipioOptions}
                      value={selectedMunicipioOption}
                      onChange={(opt) => setValue('municipio', opt ? opt.value : '')}
                      isClearable
                      isSearchable
                      styles={droneSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isDisabled={loadingMunicipios}
                    />
                    <input
                      type="hidden"
                      {...register('municipio', { required: 'Município é obrigatorio' })}
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    className="input"
                    placeholder="Ex: Sorriso"
                    {...register('municipio')}
                  />
                )}
                {errors.municipio && (
                  <p className="text-red-500 text-sm mt-1">{errors.municipio.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dados da Aplicação */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">{operationSectionTitle}</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {APPLICATION_FIELDS_TYPES.includes(serviceType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horas de Aplicação *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className={`input bg-gray-100 ${errors.application_hours ? 'border-red-500' : ''}`}
                    readOnly
                    {...register('application_hours', {
                      required: 'Horas de aplicação é obrigatoria',
                    })}
                  />
                  {errors.application_hours && (
                    <p className="text-red-500 text-sm mt-1">{errors.application_hours.message}</p>
                  )}
                </div>
              )}

              {CULTURE_FIELD_TYPES.includes(serviceType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cultura *</label>
                  <Select
                    className={`react-select-container ${errors.culture ? 'border-red-500' : ''}`}
                    classNamePrefix="react-select"
                    placeholder={
                      culturas.length ? 'Selecione uma cultura...' : 'Carregando culturas...'
                    }
                    options={culturas.map((c) => ({ value: c, label: c }))}
                    value={
                      watch('culture') ? { value: watch('culture'), label: watch('culture') } : null
                    }
                    onChange={(opt) => setValue('culture', opt ? opt.value : '')}
                    isClearable
                    isSearchable
                    styles={droneSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isDisabled={culturas.length === 0}
                  />
                  <input
                    type="hidden"
                    {...register('culture', { required: 'Cultura é obrigatoria' })}
                  />
                  {errors.culture && (
                    <p className="text-red-500 text-sm mt-1">{errors.culture.message}</p>
                  )}
                </div>
              )}

              {(serviceType === 'APLICAÇÃO DE AGROTÓXICO' ||
                serviceType === 'APLICAÇÃO DE BIOINSUMO' ||
                serviceType === 'APLICAÇÃO DE FERTILIZANTE' ||
                serviceType === 'OUTROS') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Volume (l/ha) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className={`input ${errors.volume_lha ? 'border-red-500' : ''}`}
                    {...register('volume_lha', { required: 'Volume é obrigatorio' })}
                  />
                  {errors.volume_lha && (
                    <p className="text-red-500 text-sm mt-1">{errors.volume_lha.message}</p>
                  )}
                </div>
              )}
            </div>

            <input type="hidden" {...register('application_products')} />
            {serviceType === 'APLICAÇÃO DE AGROTÓXICO' && (
              <>
                <input type="hidden" {...register('agrotoxico_nome_comercial')} />
                <input type="hidden" {...register('agrotoxico_classe')} />
                <input type="hidden" {...register('dosage_value')} />
                <input type="hidden" {...register('dosage_unit')} />
              </>
            )}

            <div className="mt-6 border-t pt-6 space-y-6">
              {/* Aplicação de Agrotóxico */}
              {serviceType === 'APLICAÇÃO DE AGROTÓXICO' && (
                <div data-testid="agrotoxic-products" className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm font-semibold text-gray-900">Produtos</div>
                  </div>

                  <div className="space-y-4">
                    {agrotoxicoProducts.map((p, idx) => (
                      <div key={idx} className="rounded-md border p-4">
                        <div className="flex items-center justify-between gap-4 mb-4">
                          <div className="text-sm font-medium text-gray-900">Produto {idx + 1}</div>
                          {idx > 0 && (
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => {
                                const next = agrotoxicoProducts.filter((_, i) => i !== idx);
                                setAgrotoxicoProducts(next);
                              }}
                            >
                              Remover
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                          <div className="lg:col-span-5">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nome Comercial
                            </label>
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              placeholder="Selecione ou digite para buscar..."
                              options={marcasComerciais}
                              value={
                                p?.nome_comercial
                                  ? marcasComerciais.find((m) => m.value === p.nome_comercial) || {
                                      label: p.nome_comercial,
                                      value: p.nome_comercial,
                                      classe: p?.classe,
                                    }
                                  : null
                              }
                              onChange={(option) => {
                                const nome = option ? option.label : '';
                                const next = agrotoxicoProducts.map((item, i) => {
                                  if (i !== idx) return item;
                                  const updated = { ...item, nome_comercial: nome };
                                  if (option?.classe) updated.classe = option.classe;
                                  else {
                                    const mapped = localProductClassMap[normalizeKey(nome)];
                                    if (mapped) updated.classe = mapped;
                                  }
                                  return updated;
                                });
                                setAgrotoxicoProducts(next);
                              }}
                              isClearable
                              isLoading={marcasComerciais.length === 0}
                              isSearchable
                              styles={droneSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>

                          <div className="lg:col-span-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Classe/Categoria Agronômica
                            </label>
                            <input
                              type="text"
                              className="input"
                              value={p?.classe || ''}
                              onChange={(e) => {
                                const next = agrotoxicoProducts.map((item, i) =>
                                  i === idx ? { ...item, classe: e.target.value } : item
                                );
                                setAgrotoxicoProducts(next);
                              }}
                            />
                          </div>

                          <div className="lg:col-span-3">
                            <div className="grid grid-cols-12 gap-2">
                              <div className="col-span-7">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Dosagem
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="input w-full"
                                  value={p?.dosage_value ?? ''}
                                  onChange={(e) => {
                                    const next = agrotoxicoProducts.map((item, i) =>
                                      i === idx ? { ...item, dosage_value: e.target.value } : item
                                    );
                                    setAgrotoxicoProducts(next);
                                  }}
                                />
                              </div>
                              <div className="col-span-5">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Unidade
                                </label>
                                <select
                                  className="input w-full"
                                  value={p?.dosage_unit || ''}
                                  onChange={(e) => {
                                    const next = agrotoxicoProducts.map((item, i) =>
                                      i === idx ? { ...item, dosage_unit: e.target.value } : item
                                    );
                                    setAgrotoxicoProducts(next);
                                  }}
                                >
                                  <option value="" disabled>
                                    Selecione
                                  </option>
                                  {DOSAGE_UNITS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="flex gap-3">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() =>
                          setAgrotoxicoProducts([
                            ...agrotoxicoProducts,
                            { nome_comercial: '', classe: '', dosage_value: '', dosage_unit: '' },
                          ])
                        }
                      >
                        Adicionar produto
                      </button>
                      {adjuvantes.length === 0 && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() =>
                            setAdjuvantes([
                              ...adjuvantes,
                              { nome: '', dosage_value: '', dosage_unit: '' },
                            ])
                          }
                        >
                          Adicionar adjuvante
                        </button>
                      )}
                    </div>

                    {adjuvantes.length > 0 && (
                      <div className="space-y-4">
                        <div className="text-sm font-semibold text-gray-900">Adjuvantes</div>
                        {adjuvantes.map((a, idx) => (
                          <div key={idx} className="rounded-md border p-4">
                            <div className="flex items-center justify-between gap-4 mb-4">
                              <div className="text-sm font-medium text-gray-900">
                                Adjuvante {idx + 1}
                              </div>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() =>
                                  setAdjuvantes(adjuvantes.filter((_, i) => i !== idx))
                                }
                              >
                                Remover
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                              <div className="lg:col-span-7">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Nome do Adjuvante
                                </label>
                                <input
                                  type="text"
                                  className="input"
                                  value={a?.nome || ''}
                                  onChange={(e) =>
                                    setAdjuvantes(
                                      adjuvantes.map((it, i) =>
                                        i === idx ? { ...it, nome: e.target.value } : it
                                      )
                                    )
                                  }
                                />
                              </div>
                              <div className="lg:col-span-5">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Dosagem
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    className="input flex-1"
                                    value={a?.dosage_value ?? ''}
                                    onChange={(e) =>
                                      setAdjuvantes(
                                        adjuvantes.map((it, i) =>
                                          i === idx ? { ...it, dosage_value: e.target.value } : it
                                        )
                                      )
                                    }
                                  />
                                  <select
                                    className="input w-28"
                                    value={a?.dosage_unit || ''}
                                    onChange={(e) =>
                                      setAdjuvantes(
                                        adjuvantes.map((it, i) =>
                                          i === idx ? { ...it, dosage_unit: e.target.value } : it
                                        )
                                      )
                                    }
                                  >
                                    <option value="">Unidade</option>
                                    {ADJUVANT_DOSAGE_UNITS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() =>
                              setAdjuvantes([
                                ...adjuvantes,
                                { nome: '', dosage_value: '', dosage_unit: '' },
                              ])
                            }
                          >
                            Adicionar adjuvante
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bioinsumo */}
              {serviceType === 'APLICAÇÃO DE BIOINSUMO' && (
                <div data-testid="bioinsumo-products" className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm font-semibold text-gray-900">Bioinsumos</div>
                  </div>

                  <input type="hidden" {...register('bioinsumo_nome_comercial')} />
                  <input type="hidden" {...register('bioinsumo_classe')} />
                  <input type="hidden" {...register('dosage_value')} />
                  <input type="hidden" {...register('dosage_unit')} />

                  <div className="space-y-4">
                    {bioinsumoProducts.map((p, idx) => (
                      <div key={idx} className="rounded-md border p-4">
                        <div className="flex items-center justify-between gap-4 mb-4">
                          <div className="text-sm font-medium text-gray-900">
                            Bioinsumo {idx + 1}
                          </div>
                          {idx > 0 && (
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() =>
                                setBioinsumoProducts(bioinsumoProducts.filter((_, i) => i !== idx))
                              }
                            >
                              Remover
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                          <div className="lg:col-span-5">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nome Comercial
                            </label>
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              placeholder="Selecione ou digite para buscar..."
                              options={marcasComerciaisBioinsumo}
                              value={
                                p?.nome_comercial
                                  ? marcasComerciaisBioinsumo.find(
                                      (m) => m.value === p.nome_comercial
                                    ) || {
                                      label: p.nome_comercial,
                                      value: p.nome_comercial,
                                      classe: p?.classe,
                                    }
                                  : null
                              }
                              onChange={(option) => {
                                const nome = option ? option.label : '';
                                const classe =
                                  option?.classe || localProductClassMap[normalizeKey(nome)] || '';
                                const next = bioinsumoProducts.map((item, i) =>
                                  i === idx
                                    ? {
                                        ...item,
                                        nome_comercial: nome,
                                        classe: classe || item.classe,
                                      }
                                    : item
                                );
                                setBioinsumoProducts(next);
                              }}
                              isClearable
                              isLoading={marcasComerciaisBioinsumo.length === 0}
                              isSearchable
                              styles={droneSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>

                          <div className="lg:col-span-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Classe Bioinsumo
                            </label>
                            <input
                              type="text"
                              className="input"
                              value={p?.classe || ''}
                              onChange={(e) =>
                                setBioinsumoProducts(
                                  bioinsumoProducts.map((it, i) =>
                                    i === idx ? { ...it, classe: e.target.value } : it
                                  )
                                )
                              }
                            />
                          </div>

                          <div className="lg:col-span-3">
                            <div className="grid grid-cols-12 gap-2">
                              <div className="col-span-7">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Dosagem
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="input w-full"
                                  value={p?.dosage_value ?? ''}
                                  onChange={(e) =>
                                    setBioinsumoProducts(
                                      bioinsumoProducts.map((it, i) =>
                                        i === idx ? { ...it, dosage_value: e.target.value } : it
                                      )
                                    )
                                  }
                                />
                              </div>
                              <div className="col-span-5">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Unidade
                                </label>
                                <select
                                  className="input w-full"
                                  value={p?.dosage_unit || ''}
                                  onChange={(e) =>
                                    setBioinsumoProducts(
                                      bioinsumoProducts.map((it, i) =>
                                        i === idx ? { ...it, dosage_unit: e.target.value } : it
                                      )
                                    )
                                  }
                                >
                                  <option value="" disabled>
                                    Selecione
                                  </option>
                                  {DOSAGE_UNITS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        setBioinsumoProducts([
                          ...bioinsumoProducts,
                          { nome_comercial: '', classe: '', dosage_value: '', dosage_unit: '' },
                        ])
                      }
                    >
                      Adicionar bioinsumo
                    </button>
                  </div>
                </div>
              )}

              {/* Fertilizantes / Outros */}
              {(serviceType === 'APLICAÇÃO DE FERTILIZANTE' || serviceType === 'OUTROS') && (
                <div data-testid="fertilizante-products" className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm font-semibold text-gray-900">
                      {serviceType === 'OUTROS' ? 'Outros' : 'Fertilizante'}
                    </div>
                  </div>

                  <input type="hidden" {...register('fertilizantes_outros')} />
                  <input type="hidden" {...register('dosage_value')} />
                  <input type="hidden" {...register('dosage_unit')} />

                  <div className="space-y-4">
                    {fertilizanteProducts.map((p, idx) => (
                      <div key={idx} className="rounded-md border p-4">
                        <div className="flex items-center justify-between gap-4 mb-4">
                          <div className="text-sm font-medium text-gray-900">
                            {serviceType === 'OUTROS'
                              ? `Outro ${idx + 1}`
                              : `Fertilizante ${idx + 1}`}
                          </div>
                          {idx > 0 && (
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() =>
                                setFertilizanteProducts(
                                  fertilizanteProducts.filter((_, i) => i !== idx)
                                )
                              }
                            >
                              Remover
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                          <div className="lg:col-span-9">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nome
                            </label>
                            <input
                              type="text"
                              className="input"
                              value={p?.nome_comercial || ''}
                              onChange={(e) =>
                                setFertilizanteProducts(
                                  fertilizanteProducts.map((it, i) =>
                                    i === idx ? { ...it, nome_comercial: e.target.value } : it
                                  )
                                )
                              }
                            />
                          </div>

                          <div className="lg:col-span-3">
                            <div className="grid grid-cols-12 gap-2">
                              <div className="col-span-7">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Dosagem
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="input w-full"
                                  value={p?.dosage_value ?? ''}
                                  onChange={(e) =>
                                    setFertilizanteProducts(
                                      fertilizanteProducts.map((it, i) =>
                                        i === idx ? { ...it, dosage_value: e.target.value } : it
                                      )
                                    )
                                  }
                                />
                              </div>
                              <div className="col-span-5">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Unidade
                                </label>
                                <select
                                  className="input w-full"
                                  value={p?.dosage_unit || ''}
                                  onChange={(e) =>
                                    setFertilizanteProducts(
                                      fertilizanteProducts.map((it, i) =>
                                        i === idx ? { ...it, dosage_unit: e.target.value } : it
                                      )
                                    )
                                  }
                                >
                                  <option value="" disabled>
                                    Selecione
                                  </option>
                                  {DOSAGE_UNITS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        setFertilizanteProducts([
                          ...fertilizanteProducts,
                          { nome_comercial: '', dosage_value: '', dosage_unit: '' },
                        ])
                      }
                    >
                      {serviceType === 'OUTROS' ? 'Adicionar outro' : 'Adicionar fertilizante'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Weather Conditions */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Condições Meteorológicas</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Thermometer className="h-4 w-4 inline mr-1" />
                  Temperatura (°C)
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="input"
                  placeholder="Ex: 25.5"
                  {...register('temperature')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Wind className="h-4 w-4 inline mr-1" />
                  Velocidade do Vento (km/h)
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="input"
                  placeholder="Ex: 15.5"
                  {...register('wind_speed')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Direção do Vento
                </label>
                <select className="input" {...register('wind_direction')}>
                  <option value="" disabled>
                    Selecione
                  </option>
                  <option value="N">N</option>
                  <option value="NE">NE</option>
                  <option value="L">L</option>
                  <option value="SE">SE</option>
                  <option value="S">S</option>
                  <option value="SO">SO</option>
                  <option value="O">O</option>
                  <option value="NO">NO</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Umidade Relativa do Ar (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="input"
                  placeholder="Ex: 55"
                  {...register('relative_humidity')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Documentos</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Mapa de Aplicação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mapa de Aplicação
                  {applicationMaps.length > 0 && (
                    <span className="ml-2 text-xs text-gray-500">({applicationMaps.length}/5)</span>
                  )}
                </label>

                {/* Lista de arquivos existentes */}
                {isEdit && (
                  <FileAttachmentList
                    files={applicationMaps}
                    kind="application_map"
                    onDownload={downloadAttachment}
                    onDelete={deleteAttachment}
                    isLoading={loadingFlight}
                  />
                )}

                {/* Lista de arquivos novos selecionados */}
                {applicationMapFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-blue-600 font-medium">
                      Novos arquivos ({applicationMapFiles.length})
                    </div>
                    {applicationMapFiles.map((file, idx) => (
                      <div
                        key={`new-app-${idx}`}
                        className="flex items-center justify-between bg-blue-50 rounded-lg p-2 border border-blue-200"
                      >
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <File className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newFiles = [...applicationMapFiles];
                            newFiles.splice(idx, 1);
                            setApplicationMapFiles(newFiles);
                          }}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Input para novos arquivos */}
                <div className="mt-3">
                  <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                    <div className="flex items-center space-x-2">
                      <Upload className="h-5 w-5 text-gray-400" />
                      <div className="text-sm text-gray-600">
                        <span className="font-medium text-blue-600">Clique para selecionar</span>
                        <span className="text-gray-500"> ou arraste arquivos</span>
                      </div>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="application/pdf,image/jpeg,image/png"
                      multiple
                      disabled={applicationMapFiles.length + applicationMaps.length >= 5}
                      onChange={(e) => {
                        const newFiles = e.target.files;
                        if (newFiles && newFiles.length > 0) {
                          handleFileSelection(
                            'application_map',
                            newFiles,
                            applicationMapFiles,
                            setApplicationMapFiles
                          );
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, JPG ou PNG. Máximo 3 arquivos (5MB cada)
                  </p>
                </div>
              </div>

              {/* Receituário Agronômico */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Receituário Agronômico
                  {agronomicPrescriptions.length > 0 && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({agronomicPrescriptions.length}/5)
                    </span>
                  )}
                </label>

                {/* Lista de arquivos existentes */}
                {isEdit && (
                  <FileAttachmentList
                    files={agronomicPrescriptions}
                    kind="agronomic_prescription"
                    onDownload={downloadAttachment}
                    onDelete={deleteAttachment}
                    isLoading={loadingFlight}
                  />
                )}

                {/* Lista de arquivos novos selecionados */}
                {agronomicPrescriptionFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-blue-600 font-medium">
                      Novos arquivos ({agronomicPrescriptionFiles.length})
                    </div>
                    {agronomicPrescriptionFiles.map((file, idx) => (
                      <div
                        key={`new-agro-${idx}`}
                        className="flex items-center justify-between bg-blue-50 rounded-lg p-2 border border-blue-200"
                      >
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <File className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newFiles = [...agronomicPrescriptionFiles];
                            newFiles.splice(idx, 1);
                            setAgronomicPrescriptionFiles(newFiles);
                          }}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Input para novos arquivos */}
                <div className="mt-3">
                  <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                    <div className="flex items-center space-x-2">
                      <Upload className="h-5 w-5 text-gray-400" />
                      <div className="text-sm text-gray-600">
                        <span className="font-medium text-blue-600">Clique para selecionar</span>
                        <span className="text-gray-500"> ou arraste arquivos</span>
                      </div>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="application/pdf,image/jpeg,image/png"
                      multiple
                      disabled={
                        agronomicPrescriptionFiles.length + agronomicPrescriptions.length >= 5
                      }
                      onChange={(e) => {
                        const newFiles = e.target.files;
                        if (newFiles && newFiles.length > 0) {
                          handleFileSelection(
                            'agronomic_prescription',
                            newFiles,
                            agronomicPrescriptionFiles,
                            setAgronomicPrescriptionFiles
                          );
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, JPG ou PNG. Máximo 3 arquivos (5MB cada)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Observations */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Observações</h3>
          </div>
          <div className="card-body">
            <textarea
              className="input"
              rows={4}
              placeholder="Observações adicionais sobre o voo, problemas encontrados, etc."
              {...register('observations')}
              style={{ minHeight: '120px', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          {isEdit && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await downloadFlightPdf({ id, flight_date: watch('flight_date') });
                } catch (error) {
                  toast.error(error.response?.data?.error || 'Erro ao gerar PDF');
                }
              }}
              className="btn-secondary"
              disabled={isLoading}
            >
              Baixar PDF
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/flights')}
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? (
              <LoadingSpinner size="sm" className="mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEdit ? 'Atualizar' : 'Salvar'} Voo
          </button>
        </div>
      </form>
      {showMapModal && (
        <CoordinateMapModal
          center={mapCenter}
          marker={mapMarker}
          onClose={() => setShowMapModal(false)}
          onSelect={(lat, lon) => {
            setValue('latitude', String(lat));
            setValue('longitude', String(lon));
            setValue('coordinates', `${lat.toFixed(6)}, ${lon.toFixed(6)}`);
            setShowMapModal(false);
          }}
        />
      )}
    </div>
  );
};

/**
 * MapTileLayer Component
 * Renders the appropriate tile layer based on activeMapLayer state
 * Uses react-leaflet TileLayer component for proper rendering
 */
const MapTileLayer = ({ activeLayer, mapInstance, setMapInstance, setCurrentLayer }) => {
  const map = useMap();

  // Store map instance when available
  React.useEffect(() => {
    if (map && !mapInstance) {
      setMapInstance(map);
    }
  }, [map, mapInstance, setMapInstance]);

  // Determine tile URL and attribution based on active layer
  const getTileConfig = () => {
    switch (activeLayer) {
      case 'satellite':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution:
            '&copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a>',
        };
      case 'hybrid':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution:
            '&copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a>',
        };
      case 'streets':
      default:
        return {
          url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OSM</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener noreferrer">CARTO</a>',
        };
    }
  };

  const tileConfig = getTileConfig();

  return <TileLayer url={tileConfig.url} attribution={tileConfig.attribution} maxZoom={19} />;
};

const CoordinateMapModal = ({ center, marker, onClose, onSelect }) => {
  const [selected, setSelected] = useState(marker);
  const [activeMapLayer, setActiveMapLayer] = useState(() => getInitialLayerPreference());
  const [mapInstance, setMapInstance] = useState(null);
  const [currentLayer, setCurrentLayer] = useState(null);

  /**
   * Handle map layer change
   * Preserves current center and zoom when switching layers
   * Saves preference to localStorage
   */
  const handleLayerChange = (layerType) => {
    if (!mapInstance) return;

    // Preserve current view state
    const currentCenter = mapInstance.getCenter();
    const currentZoom = mapInstance.getZoom();

    // Remove current layer
    if (currentLayer) {
      mapInstance.removeLayer(currentLayer);
    }

    // Create and add new layer
    let newLayer;
    switch (layerType) {
      case 'satellite':
        newLayer = createEsriLayer();
        break;
      case 'hybrid':
        newLayer = createHybridLayer();
        break;
      case 'streets':
      default:
        newLayer = createCartoLayer();
        break;
    }

    newLayer.addTo(mapInstance);
    setCurrentLayer(newLayer);

    // Restore view state
    mapInstance.setView(currentCenter, currentZoom);
    setActiveMapLayer(layerType);

    // Save preference to localStorage
    setLayerPreference(layerType);
  };

  const ClickHandler = () => {
    useMapEvents({
      click: (e) => {
        setSelected([e.latlng.lat, e.latlng.lng]);
      },
    });
    return null;
  };

  /**
   * MapController component to initialize map instance and default layer
   */
  const MapController = () => {
    const map = useMapEvents({
      load: () => {
        setMapInstance(map);
        // Initialize with default layer based on preference
        const initialLayer =
          activeMapLayer === 'streets'
            ? createCartoLayer()
            : activeMapLayer === 'satellite'
              ? createEsriLayer()
              : createHybridLayer();
        initialLayer.addTo(map);
        setCurrentLayer(initialLayer);
      },
    });
    return null;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Selecionar coordenada no mapa</h3>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
        <div className="relative h-[60vh] w-full overflow-hidden rounded-md border">
          <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }}>
            <MapTileLayer
              activeLayer={activeMapLayer}
              mapInstance={mapInstance}
              setMapInstance={setMapInstance}
              setCurrentLayer={setCurrentLayer}
            />
            <ClickHandler />
            {selected && <Marker position={selected} />}
          </MapContainer>
          <MapLayerControl
            activeLayer={activeMapLayer}
            onLayerChange={handleLayerChange}
            mapInstance={mapInstance}
          />
          <MapAttribution activeLayer={activeMapLayer} />
        </div>
        <div className="flex justify-end space-x-3 pt-4">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!selected}
            onClick={() => {
              if (!selected) return;
              onSelect(selected[0], selected[1]);
            }}
          >
            Usar ponto
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightForm;
